/**
 * React Query hooks for admin review moderation API.
 *
 * All query keys include tenant to prevent cross-tenant cache collisions.
 * All queries are disabled until tenant is available (auth hydration).
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query"
import { useAuthStore } from "@/lib/store/authStore"
import { toast } from "sonner"
import { reviewApi } from "./reviewApi"
import type {
  ReviewListResponse,
  ReviewStatus,
} from "./reviewTypes"

function useTenant() {
  return useAuthStore((s) => s.currentTenant)
}

export const reviewQueryKeys = {
  all: (tenant: string | null) => ["reviews", tenant] as const,
  // tenant BEFORE "list" so `all(tenant)` is a true PREFIX of `list(...)`.
  // TanStack invalidation is prefix-matched element-by-element; the old
  // ["reviews","list",tenant,...] shape was never matched by ["reviews",tenant]
  // → approve/reply never refetched (stuck-until-hard-refresh bug).
  list: (tenant: string | null, status: ReviewStatus, page: number, search: string) =>
    ["reviews", tenant, "list", status, page, search] as const,
}

export function useReviewList(
  status: ReviewStatus = "Pending",
  page = 1,
  limit = 20,
  search = "",
) {
  const tenant = useTenant()

  return useQuery<ReviewListResponse>({
    queryKey: reviewQueryKeys.list(tenant, status, page, search),
    queryFn: () => reviewApi.list(status, page, limit, search),
    enabled: !!tenant,
    placeholderData: keepPreviousData,
  })
}

export function useModerateReview() {
  const queryClient = useQueryClient()
  const tenant = useTenant()

  return useMutation({
    mutationFn: ({
      reviewName,
      action,
    }: {
      reviewName: string
      action: "approve" | "reject"
    }) => reviewApi.moderate(reviewName, action),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({
        queryKey: reviewQueryKeys.all(tenant),
      })
      const label = variables.action === "approve" ? "approved" : "rejected"
      toast.success(`Review ${label}`)
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to moderate review")
    },
  })
}

export function useReplyToReview() {
  const queryClient = useQueryClient()
  const tenant = useTenant()

  return useMutation({
    mutationFn: ({
      reviewName,
      replyText,
    }: {
      reviewName: string
      replyText: string
    }) => reviewApi.reply(reviewName, replyText),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: reviewQueryKeys.all(tenant),
      })
      toast.success("Reply posted")
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to post reply")
    },
  })
}

export function useDeleteReply() {
  const queryClient = useQueryClient()
  const tenant = useTenant()

  return useMutation({
    mutationFn: (reviewName: string) => reviewApi.deleteReply(reviewName),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: reviewQueryKeys.all(tenant),
      })
      toast.success("Reply deleted")
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete reply")
    },
  })
}

export function useDeleteReview() {
  const queryClient = useQueryClient()
  const tenant = useTenant()

  return useMutation({
    mutationFn: (reviewName: string) => reviewApi.delete(reviewName),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: reviewQueryKeys.all(tenant),
      })
      toast.success("Review deleted")
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete review")
    },
  })
}

export function useBulkModerateReviews() {
  const queryClient = useQueryClient()
  const tenant = useTenant()

  return useMutation({
    mutationFn: ({
      reviewNames,
      action,
    }: {
      reviewNames: string[]
      action: "approve" | "reject"
    }) => reviewApi.bulkModerate(reviewNames, action),
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({
        queryKey: reviewQueryKeys.all(tenant),
      })
      const label = variables.action === "approve" ? "approved" : "rejected"
      // backend admin_bulk_moderate returns { success, count } (not moderated_count)
      toast.success(`${result.count} review(s) ${label}`)
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to moderate reviews")
    },
  })
}
